import { DEFAULT } from '../../shared/constants/defaults';
import { NestedQuota } from '../../shared/interfaces/quota.interface';
import { NestedQuotaGroupSeparator } from '../multi-quota-nesting-modal/domain/nested-quota-group-separator';

export class SurveyNestedQuotas {
  constructor(private _nestedQuotas: NestedQuota[]) {}

  public get nestedQuotas(): NestedQuota[] {
    return this._nestedQuotas || [];
  }

  public kind(id: string): SurveyNestedQuotas {
    const grouped = this._group(id);
    return new SurveyNestedQuotas(grouped);
  }

  public ungrouped(): SurveyNestedQuotas {
    const flatten = this.nestedQuotas
      .map((q) =>
        q.individual_quotas?.length
          ? new NestedQuotaGroupSeparator(q).ungroup()
          : q
      )
      .flat();
    return new SurveyNestedQuotas(flatten);
  }

  public get(): NestedQuota[] {
    return this.nestedQuotas;
  }

  public allocation(): number {
    return this.nestedQuotas.reduce(
      (allocation, nestedQuota) =>
        allocation + (nestedQuota.required_count || nestedQuota.number || 0),
      0
    );
  }

  public percentage(): number {
    return this.nestedQuotas.reduce(
      (percentage, quota) => percentage + (quota.percentage || 0),
      0
    );
  }

  public titles(): string[] {
    return this.nestedQuotas
      .filter((nestedQuota) => nestedQuota.quota_title)
      .map(({ quota_title }) => quota_title as string);
  }

  public filter(quotas: NestedQuota[]): NestedQuota[] {
    return this.nestedQuotas.filter(
      (nestedQuota) =>
        !quotas.some(
          (quota) => quota.buyer_quota_id === nestedQuota.buyer_quota_id
        )
    );
  }

  public hasGroupedQuotas(): boolean {
    return this.nestedQuotas.some(
      (nestedQuota) => !!nestedQuota.individual_quotas?.length
    );
  }

  public qualificationIds(): number[] {
    return [
      ...new Set(
        this.nestedQuotas
          .map((nestedQuota) =>
            nestedQuota.criteria.map((criterion) =>
              Number(criterion.qualification_code)
            )
          )
          .flat()
      ),
    ];
  }

  public hasQualification(qualificationCode: number) {
    return this.qualificationIds().some(
      (code) => Number(code) === Number(qualificationCode)
    );
  }

  public groupIds(): string[] {
    return [
      ...new Set(
        this._nestedQuotas
          .map(
            (nestedQuota) => nestedQuota.group_key || DEFAULT.NESTED_GROUP_KEY
          )
          .filter((id) => !!id)
      ),
    ];
  }

  public sortByTitle(): SurveyNestedQuotas {
    const sorted = this.nestedQuotas.sort((currQuota, prevQuota) => {
      if (!currQuota.quota_title) {
        return 1;
      }

      if (!prevQuota.quota_title) {
        return -1;
      }

      return currQuota.quota_title.localeCompare(prevQuota.quota_title);
    });

    return new SurveyNestedQuotas(sorted);
  }

  private _group(id: string): NestedQuota[] {
    if (id === DEFAULT.NESTED_GROUP_KEY) {
      return this.nestedQuotas.filter(
        (nestedQuota) =>
          !nestedQuota.group_key ||
          nestedQuota.group_key === DEFAULT.NESTED_GROUP_KEY
      );
    }
    return this.nestedQuotas.filter(
      (nestedQuota) => nestedQuota.group_key === id
    );
  }
}
